import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import { sortBy } from 'underscore';

import {
  ImageResource,
  QuizAnswer,
  VideoResource,
} from '~teacher/models/quizzes';

import localAxios from '../localAxios';
import { Answer, CreateQuestionPayload } from '../requests';
import { CreateQuestionResponse, QuestionResponse } from '../responses';

export type QuizQuestionUrl = ({
  quizId,
  questionId,
}: {
  quizId: number | string;
  questionId?: number | string | (number | string)[];
}) => string;

export type QuizQuestionOrderUrl = ({
  quizId,
  questionId,
}: {
  quizId: string | number;
  questionId: string | number;
}) => string;

// TODO: Add MATCH_ANSWER_ORDER_OFFSET to Socrative Utils
const MATCH_ANSWER_ORDER_OFFSET = 1000;

function resequenceAnswerOrder(answers: Array<QuizAnswer>) {
  // TODO UPDATE answer config too.
  const sortedAnswers = sortBy(answers, (answer) => answer.order);

  // -1 if there are no match answers.
  const firstMatchAnswerIndex = sortedAnswers.findIndex(
    (answer) => answer.order > MATCH_ANSWER_ORDER_OFFSET
  );

  return sortedAnswers.map((answer, index) => ({
    ...answer,
    order:
      firstMatchAnswerIndex === -1 || index < firstMatchAnswerIndex
        ? index + 1
        : index + 1 - firstMatchAnswerIndex + MATCH_ANSWER_ORDER_OFFSET,
  }));
}

function removeEmptyAnswers(answers: Array<QuizAnswer>) {
  const filteredAnswers = answers.filter(
    (answer) => (answer.text && answer.text.trim().length > 0) || answer.image
  );
  return resequenceAnswerOrder(filteredAnswers);
}

const createAnswerPayload = (answer: QuizAnswer) => {
  const answerPayload: Answer = {
    image: answer.image ?? null,
    order: answer.order,
    text: answer.text ? answer.text.trim() : '',
    is_correct: !!answer.isCorrect,
  };
  if (answer.id && typeof answer.id === 'number') {
    answerPayload.id = answer.id as number;
  }

  return answerPayload;
};

interface QuestionPayloadParams {
  answers: QuizAnswer[];
  explanation: string;
  explanationImage: ImageResource;
  gradingWeight: number | string;
  questionAudio: string;
  questionImage: ImageResource;
  questionText: string;
  questionVideo: VideoResource;
}

function createQuestionPayload({
  explanation,
  explanationImage,
  questionText,
  questionImage,
  gradingWeight,
  questionVideo,
  questionAudio,
  answers,
}: QuestionPayloadParams): CreateQuestionPayload {
  return {
    explanation: explanation ?? '',
    question_text: questionText ?? '',
    question_image: questionImage ?? null,
    explanation_image: explanationImage ?? null,
    ...(questionVideo && {
      question_video: {
        url: questionVideo.url,
        data: {
          start: questionVideo.start,
          end: questionVideo.end,
          provider: questionVideo.provider,
          duration: questionVideo.duration,
        },
      },
    }),
    ...(questionAudio && { question_audio: { url: questionAudio } }),
    grading_weight:
      gradingWeight === '' ||
      gradingWeight === null ||
      gradingWeight === undefined
        ? 1
        : parseFloat(gradingWeight.toString()),
    answers: removeEmptyAnswers(answers).map(createAnswerPayload),
  };
}

interface CreateQuestionParams {
  answers?: QuizAnswer[];
  explanation: string;
  explanationImage?: ImageResource;
  gradingWeight?: number;
  order: number;
  questionAudio?: string;
  questionImage?: ImageResource;
  questionText: string;
  questionVideo?: VideoResource;
  quizId: string | number;
  quizQuestionUrl: QuizQuestionUrl;
  type: QuestionTypes;
}

export async function createQuestion({
  quizId,
  type,
  order,
  explanation,
  explanationImage,
  questionText,
  questionImage,
  gradingWeight,
  answers,
  questionAudio,
  questionVideo,
  quizQuestionUrl,
}: CreateQuestionParams) {
  const url = quizQuestionUrl({ quizId });
  const payload = createQuestionPayload({
    explanation: explanation,
    explanationImage: explanationImage,
    questionText: questionText,
    questionImage: questionImage,
    gradingWeight: gradingWeight,
    questionVideo: questionVideo,
    questionAudio: questionAudio,
    answers: answers,
  });
  const response = await localAxios.post<CreateQuestionResponse>(url, payload, {
    params: { type, order },
  });
  const { data } = response;
  return {
    question: data.questions[0],
    orders: data.orders,
  };
}

interface UpdateQuestionParams {
  answers: QuizAnswer[];
  explanation: string;
  explanationImage: ImageResource;
  gradingWeight: number;
  questionAudio?: string;
  questionId: number;
  questionImage: ImageResource;
  questionText: string;
  questionVideo?: VideoResource;
  quizId: string | number;
  quizQuestionUrl: QuizQuestionUrl;
}

export async function updateQuestion({
  quizId,
  questionId,
  explanation,
  explanationImage,
  questionText,
  questionImage,
  gradingWeight,
  answers,
  quizQuestionUrl,
  questionAudio,
  questionVideo,
}: UpdateQuestionParams) {
  const url = quizQuestionUrl({ questionId, quizId });
  const payload = createQuestionPayload({
    explanation: explanation,
    explanationImage: explanationImage,
    questionText: questionText,
    questionImage: questionImage,
    gradingWeight: gradingWeight,
    questionVideo: questionVideo,
    questionAudio: questionAudio,
    answers: answers,
  });
  const response = await localAxios.put<QuestionResponse>(url, payload);
  const { data } = response;
  return data;
}

const deleteQuestion = async ({
  questionId,
  quizId,
  quizQuestionUrl,
}: {
  questionId: string | number;
  quizId: string | number;
  quizQuestionUrl: QuizQuestionUrl;
}) => {
  const url = quizQuestionUrl({ questionId, quizId });
  const response = await localAxios.delete<QuestionResponse>(url);
  const { data } = response;
  return data;
};

const moveQuestion = async ({
  quizId,
  questionId,
  order,
  quizQuestionOrderUrl,
}: {
  quizId: string | number;
  questionId: string | number;
  order: number;
  quizQuestionOrderUrl: QuizQuestionOrderUrl;
}) => {
  const url = quizQuestionOrderUrl({ quizId, questionId });
  const payload = { order };
  const response = await localAxios.put<QuestionResponse>(url, payload);
  return response.data;
};

export { deleteQuestion, moveQuestion };
